import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useState, useEffect, useRef } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import { getSaleItemSummary } from "../../../utils/salePricing";

const TAX_RATE = 0.07;

// ลำดับและป้ายชื่อสถานะ (ให้เรียงตามรูป)
const STATUS_ORDER = ["waiting", "sent", "confirm", "payed"];
const STATUS_LABEL = {
  waiting: "รอขาย",
  sent: "ส่งแล้ว",
  confirm: "ยืนยันการขายแล้ว",
  payed: "ได้รับเงินแล้ว",
};

export default function DailySalesSummary() {
  const formatDate = (date) => date.toISOString().split("T")[0];
  const printRef = useRef();

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return formatDate(d);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  const [activityRows, setActivityRows] = useState([]); // {name,count,amount,vat,total}
  const [loading, setLoading] = useState(true);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `รายงานสรุปยอดขาย_${startDate}_to_${endDate}`,
  });

  const fmt2 = (n) =>
    Number(n || 0).toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const fmtInt = (n) => Number(n || 0).toLocaleString("th-TH");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getSaleTransactions({ startDate, endDate });
        const payload = res?.data?.data ?? res?.data ?? res ?? [];
        console.log('payload', payload)

        if (Array.isArray(payload)) {
          const { activities } = summarizeSales(payload);
          setActivityRows(activities);
        } else {
          setActivityRows(payload.activities || []);
        }
      } catch (e) {
        console.error("fetch error", e);
        setActivityRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  // ปรับให้อยู่ในลำดับตามรูป และเติมสถานะที่ไม่มีข้อมูลให้มีค่า 0
  const orderedActivities = (() => {
    const map = new Map(activityRows.map((r) => [r.name, r]));
    return STATUS_ORDER.map((k) => {
      const r = map.get(k);
      return {
        name: STATUS_LABEL[k],
        count: r?.count || 0,
        amount: r?.amount || 0,
        vat: r?.vat || 0,
        total: r?.total || 0,
      };
    });
  })();

  // แถวสรุป "ทั้งหมด"
  const totalRow = orderedActivities.reduce(
    (acc, r) => {
      acc.count += r.count || 0;
      acc.amount += r.amount || 0;
      acc.vat += r.vat || 0;
      acc.total += r.total || 0;
      return acc;
    },
    { name: "ทั้งหมด", count: 0, amount: 0, vat: 0, total: 0 }
  );

  // -------- Export CSV --------
  const exportToCSV = (data, filename = "daily-sales-summary.csv") => {
    if (!data?.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((h) => `"${row[h] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = () => {
    const rowsToExport = [
      ...orderedActivities.map((r) => ({
        สถานะของบิล: r.name,
        จำนวนตั๋ว: r.count,
        "ยอดก่อนภาษี": Number(r.amount).toFixed(2),
        "ยอดภาษี": Number(r.vat).toFixed(2),
        "ยอดสุทธิ": Number(r.total).toFixed(2),
      })),
      {
        สถานะของบิล: totalRow.name,
        จำนวนตั๋ว: totalRow.count,
        "ยอดก่อนภาษี": Number(totalRow.amount).toFixed(2),
        "ยอดภาษี": Number(totalRow.vat).toFixed(2),
        "ยอดสุทธิ": Number(totalRow.total).toFixed(2),
      },
    ];
    exportToCSV(rowsToExport);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Date Filter */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span className="w-1 h-8 bg-green-600 rounded-full"></span>
              สรุปยอดขายประจำวัน
            </h1>
            
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">เริ่มต้น</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm"
                />
              </div>
              <span className="text-gray-400">-</span>
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">สิ้นสุด</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
             <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-6">
             {/* Action Buttons */}
             <div className="flex justify-end gap-3">
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-sm"
              >
                <PrintIcon fontSize="small" />
                <span>พิมพ์รายงาน</span>
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white border border-transparent rounded-xl hover:bg-green-700 transition-all font-medium shadow-sm hover:shadow-md"
              >
                <ContentCopyIcon fontSize="small" />
                <span>ข้อมูลหลัก</span>
              </button>
            </div>

            {/* Printable Content */}
            <div ref={printRef} className="print-content p-4">
              <div className="mb-6 text-center">
                <h2 className="text-xl font-bold text-gray-800">รายงานสรุปยอดขายประจำวัน</h2>
                <p className="text-gray-600 mt-1">
                  ระหว่างวันที่ {new Date(startDate).toLocaleDateString("th-TH")} - {new Date(endDate).toLocaleDateString("th-TH")}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50">
                  <h3 className="text-lg font-bold text-gray-700">รายละเอียดยอดขาย</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                          <tr>
                            <th className="px-6 py-4">สถานะของบิล</th>
                            <th className="px-6 py-4 text-right">จำนวนตั๋ว</th>
                            <th className="px-6 py-4 text-right">ยอดก่อนภาษี</th>
                            <th className="px-6 py-4 text-right">ยอดภาษี</th>
                            <th className="px-6 py-4 text-right">ยอดสุทธิ</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {orderedActivities.map((r, i) => (
                                <tr key={i} className="hover:bg-green-50/30 transition-colors">
                                    <td className="px-6 py-4 font-medium text-gray-800">{r.name}</td>
                                    <td className="px-6 py-4 text-right text-gray-600">{fmtInt(r.count)}</td>
                                    <td className="px-6 py-4 text-right text-gray-600">{fmt2(r.amount)}</td>
                                    <td className="px-6 py-4 text-right text-gray-600">{fmt2(r.vat)}</td>
                                    <td className="px-6 py-4 text-right font-medium text-gray-800">{fmt2(r.total)}</td>
                                </tr>
                            ))}
                            <tr className="bg-green-50/50 font-bold text-gray-900 border-t border-green-100">
                                <td className="px-6 py-4">{totalRow.name}</td>
                                <td className="px-6 py-4 text-right">{fmtInt(totalRow.count)}</td>
                                <td className="px-6 py-4 text-right">{fmt2(totalRow.amount)}</td>
                                <td className="px-6 py-4 text-right">{fmt2(totalRow.vat)}</td>
                                <td className="px-6 py-4 text-right text-green-700 text-base">{fmt2(totalRow.total)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------- สรุปจากลิสต์รายการขาย (ไม่คิด VAT เมื่อ isVat:false) -------- */
function summarizeSales(transactions = []) {
  const map = new Map(); // key = status

  transactions.forEach((row) => {
    const statusKey = row.status || "waiting";
    const items = Array.isArray(row.items) ? row.items : [];

    // ยอดก่อนภาษีต้องใช้สูตรเดียวกับหน้าตั๋ว รวมราคา SO และส่วนเกิน
    const amount = items.reduce(
      (acc, it) => acc + getSaleItemSummary(it).amount,
      0
    );

    // ถ้า isVat === false => ไม่คิดภาษี (vat = 0)
    // ถ้า isVat === true:
    //   - ถ้ามี row.vat มาแล้ว ใช้ตามนั้น
    //   - ถ้าไม่มี ให้คำนวณตาม TAX_RATE
    let vat = 0;
    if (row?.isVat === true) {
      if (row?.vat != null) {
        vat = Number(row.vat) || 0;
      } else {
        vat = Number((amount * TAX_RATE).toFixed(2));
      }
    }

    const total = amount + vat;

    if (!map.has(statusKey)) {
      map.set(statusKey, { name: statusKey, count: 0, amount: 0, vat: 0, total: 0 });
    }
    const r = map.get(statusKey);
    r.count += 1;
    r.amount += amount;
    r.vat += vat;
    r.total += total;
  });

  // คืน array raw (ยังไม่เรียง/ไม่เปลี่ยนชื่อ)
  const activities = Array.from(map.values());
  return { activities };
}
